import { useEffect, useState } from "react";
import { api, errMsg, ROLES, PERMISSION_LABELS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, CheckList } from "@/components/common";
import { LimitsBar } from "@/components/LimitsBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, KeyRound, Power, Mail, Phone, Layers, Search, Users } from "lucide-react";
import { matches } from "@/lib/text";

const EMPTY = { name: "", email: "", phone: "", login: "", password: "", role: "ATENDENTE", status: "Ativo", queue_ids: [], sector_ids: [], permissions: [] };

export default function UsersPage() {
  const { isAdmin } = useAuth();
  const [rows, setRows] = useState([]);
  const [queues, setQueues] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [meta, setMeta] = useState({ permissions: [], role_defaults: {} });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [pwd, setPwd] = useState({ user: null, value: "" });
  const [q, setQ] = useState("");
  const filtered = rows.filter((u) => matches(q, u.name, u.email, u.phone));

  const [limits, setLimits] = useState(null);
  const load = () => { api.get("/users").then((r) => setRows(r.data)); api.get("/tenants/me/limits").then((r) => setLimits(r.data)).catch(() => {}); };
  useEffect(() => {
    load();
    api.get("/queues").then((r) => setQueues(r.data));
    api.get("/sectors").then((r) => setSectors(r.data));
    api.get("/auth/meta").then((r) => setMeta(r.data));
  }, []);

  const openCreate = () => { setEditing(null); setForm({ ...EMPTY, permissions: meta.role_defaults.ATENDENTE || [] }); setOpen(true); };
  const openEdit = (u) => { setEditing(u); setForm({ ...EMPTY, ...u, password: "" }); setOpen(true); };
  const set = (k) => (v) => setForm({ ...form, [k]: v?.target ? v.target.value : v });
  const setRole = (role) => setForm({ ...form, role, permissions: meta.role_defaults[role] || [] });

  const submit = async () => {
    if (!form.name || !form.email || (!editing && !form.password)) return toast.error("Nome, e-mail e senha são obrigatórios");
    const payload = { ...form };
    delete payload.queue_names; delete payload.id; delete payload.tenant_id; delete payload.created_at;
    if (!payload.password) delete payload.password;
    if (!payload.login) delete payload.login;
    try {
      if (editing) { await api.put(`/users/${editing.id}`, payload); toast.success("Usuário atualizado"); }
      else { await api.post("/users", payload); toast.success("Usuário criado"); }
      setOpen(false); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const del = async (u) => {
    if (!window.confirm(`Excluir usuário "${u.name}"?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Usuário excluído"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  const toggleActive = async (u) => {
    const next = u.status === "Ativo" ? "Inativo" : "Ativo";
    if (next === "Inativo" && !window.confirm(`Inativar "${u.name}"? O usuário perde o acesso, mas o histórico é preservado.`)) return;
    try { await api.put(`/users/${u.id}`, { status: next }); toast.success(next === "Ativo" ? "Usuário reativado" : "Usuário inativado"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const resetPassword = async () => {
    if (!pwd.value || pwd.value.length < 6) return toast.error("A nova senha deve ter ao menos 6 caracteres");
    if (!window.confirm(`Redefinir a senha de "${pwd.user.name}"?`)) return;
    try { await api.put(`/users/${pwd.user.id}`, { password: pwd.value }); toast.success("Senha redefinida com sucesso"); setPwd({ user: null, value: "" }); } catch (e) { toast.error(errMsg(e)); }
  };

  const isAdminRole = form.role === "TENANT_ADMIN";

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5 sm:space-y-6">
      <PageHeader title="Usuários" subtitle="Defina perfil, filas, setores e permissões individuais de cada usuário.">
        <Button onClick={openCreate} className="bg-emerald-600 hover:bg-emerald-700" data-testid="create-user-btn"><Plus className="w-4 h-4 mr-2" /> Novo usuário</Button>
      </PageHeader>

      {limits && (limits.max_users?.limit > 0) && (
        <Card className={`p-4 border ${limits.max_users.reached ? "border-rose-300 bg-rose-50 dark:bg-rose-950/20" : "border-slate-200 dark:border-slate-800"}`} data-testid="users-limit-card">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Limites contratados</p>
          <LimitsBar usage={limits} keys={["max_users", "max_channels", "max_connections"]} />
          {limits.max_users.reached && (
            <p className="text-sm text-rose-600 mt-3" data-testid="users-limit-reached-msg">
              ⚠️ Limite de usuários atingido. Esta empresa possui limite de {limits.max_users.limit} usuários. Para adicionar novos usuários, aumente o limite no painel Super Admin.
            </p>
          )}
        </Card>
      )}

      <Card className="p-4 border-slate-200 dark:border-slate-800">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="user-search-input" placeholder="Buscar por nome, e-mail ou telefone..." className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
      </Card>

      {filtered.length === 0 ? (
        <Card className="p-10 text-center border-slate-200 dark:border-slate-800" data-testid="users-empty">
          <Users className="w-10 h-10 text-slate-300 dark:text-slate-700 mx-auto mb-3" />
          <p className="text-slate-500 dark:text-slate-400">Nenhum usuário encontrado.</p>
        </Card>
      ) : (
        <Card className="border-slate-200 dark:border-slate-800 overflow-hidden" data-testid="users-table">
          <div className="hidden md:grid grid-cols-[1fr_150px_110px_1fr_150px] gap-3 px-5 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500 bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-800">
            <span>Nome</span><span>Perfil</span><span>Status</span><span>Filas</span><span className="text-right">Ações</span>
          </div>
          <ul className="divide-y divide-slate-100 dark:divide-slate-800">
            {filtered.map((u) => (
              <li key={u.id} data-testid={`user-card-${u.email}`}
                className={`grid grid-cols-1 md:grid-cols-[1fr_150px_110px_1fr_150px] gap-x-3 gap-y-2 items-center px-4 md:px-5 py-3 hover:bg-emerald-50/50 dark:hover:bg-slate-800/60 transition-colors ${u.status === "Ativo" ? "" : "opacity-60"}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 shrink-0 rounded-full bg-emerald-500 text-white flex items-center justify-center text-sm font-bold">{u.name[0]}</div>
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-900 dark:text-white truncate">{u.name}</p>
                    <p className="text-xs text-slate-500 truncate flex items-center gap-1"><Mail className="w-3 h-3" />{u.email}</p>
                    {u.phone && <p className="text-xs text-slate-500 flex items-center gap-1"><Phone className="w-3 h-3" />{u.phone}</p>}
                  </div>
                </div>
                <div className="flex md:block items-center gap-2 pl-12 md:pl-0">
                  <Badge variant="outline" className="text-xs">{ROLES[u.role]}</Badge>
                  <Badge variant="outline" className={`md:hidden text-xs ${u.status === "Ativo" ? "text-emerald-600 border-emerald-500/30" : "text-slate-500"}`}>{u.status}</Badge>
                </div>
                <Badge variant="outline" className={`hidden md:inline-flex w-fit text-xs ${u.status === "Ativo" ? "text-emerald-600 border-emerald-500/30" : "text-slate-500"}`} data-testid={`user-status-${u.email}`}>{u.status}</Badge>
                <p className="text-xs text-slate-600 dark:text-slate-400 flex items-center gap-1.5 truncate pl-12 md:pl-0"><Layers className="w-3.5 h-3.5 shrink-0" /> {u.role === "TENANT_ADMIN" ? "Todas as filas" : (u.queue_names?.join(", ") || "Sem filas")}</p>
                <div className="flex gap-1 items-center md:justify-end pl-12 md:pl-0">
                  <Button variant="outline" size="sm" onClick={() => openEdit(u)} data-testid={`edit-user-${u.email}`}><Pencil className="w-3.5 h-3.5 mr-1" /> Editar</Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8" title="Redefinir senha" onClick={() => setPwd({ user: u, value: "" })} data-testid={`reset-password-${u.email}`}><KeyRound className="w-3.5 h-3.5" /></Button>
                  <Button size="icon" variant="ghost" className={`h-8 w-8 ${u.status === "Ativo" ? "text-amber-600" : "text-emerald-600"}`} title={u.status === "Ativo" ? "Inativar" : "Reativar"} onClick={() => toggleActive(u)} data-testid={`toggle-user-${u.email}`}><Power className="w-3.5 h-3.5" /></Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-600" title="Excluir" onClick={() => del(u)} data-testid={`delete-user-${u.email}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <Dialog open={!!pwd.user} onOpenChange={(o) => !o && setPwd({ user: null, value: "" })}>
        <DialogContent className="bg-white dark:bg-slate-900">
          <DialogHeader><DialogTitle>Redefinir senha — {pwd.user?.name}</DialogTitle></DialogHeader>
          <div><Label>Nova senha (mín. 6 caracteres)</Label><Input type="password" value={pwd.value} onChange={(e) => setPwd({ ...pwd, value: e.target.value })} data-testid="reset-password-input" autoFocus /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwd({ user: null, value: "" })}>Cancelar</Button>
            <Button onClick={resetPassword} className="bg-emerald-600 hover:bg-emerald-700" data-testid="reset-password-submit">Redefinir senha</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white dark:bg-slate-900 sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? `Editar — ${editing.name}` : "Novo usuário"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <div><Label>Nome *</Label><Input data-testid="user-name-input" value={form.name} onChange={set("name")} /></div>
              <div><Label>E-mail *</Label><Input data-testid="user-email-input" value={form.email} onChange={set("email")} /></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label>Telefone</Label><Input value={form.phone} onChange={set("phone")} /></div>
                <div><Label>Login</Label><Input data-testid="user-login-input" value={form.login} onChange={set("login")} placeholder="= e-mail" /></div>
              </div>
              <div><Label>{editing ? "Nova senha (opcional)" : "Senha *"}</Label><Input data-testid="user-password-input" type="password" value={form.password} onChange={set("password")} /></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label>Perfil</Label>
                  <Select value={form.role} onValueChange={setRole}>
                    <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white dark:bg-slate-900">
                      {isAdmin && <SelectItem value="TENANT_ADMIN">Administrador</SelectItem>}
                      <SelectItem value="SUPERVISOR">Supervisor</SelectItem>
                      <SelectItem value="ATENDENTE">Atendente</SelectItem>
                      <SelectItem value="CONSULTA">Consulta</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Status</Label>
                  <Select value={form.status} onValueChange={set("status")}>
                    <SelectTrigger data-testid="user-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white dark:bg-slate-900"><SelectItem value="Ativo">Ativo</SelectItem><SelectItem value="Inativo">Inativo</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <div className="space-y-4">
              {isAdminRole ? (
                <p className="text-sm text-slate-500 p-3 rounded-lg bg-slate-100 dark:bg-slate-800">Administradores possuem acesso completo a todas as filas, setores e permissões da empresa.</p>
              ) : (
                <>
                  <div><Label className="text-xs uppercase tracking-wider text-slate-500">Filas permitidas</Label>
                    <CheckList items={queues.map((q) => ({ id: q.id, label: q.name }))} value={form.queue_ids} onChange={set("queue_ids")} testPrefix="user-queue" /></div>
                  <div><Label className="text-xs uppercase tracking-wider text-slate-500">Setores</Label>
                    <CheckList items={sectors.map((s) => ({ id: s.id, label: s.name }))} value={form.sector_ids} onChange={set("sector_ids")} testPrefix="user-sector" /></div>
                  <div><Label className="text-xs uppercase tracking-wider text-slate-500">Permissões</Label>
                    {form.role === "CONSULTA" ? <p className="text-sm text-slate-500 p-3 rounded-lg bg-slate-100 dark:bg-slate-800">O perfil Consulta tem permissões fixas: vê a lista de OS e o dashboard das filas vinculadas, sem abrir OS nem acessar clientes.</p> : <CheckList items={meta.permissions.filter((p) => !["list_os", "view_dashboard"].includes(p)).map((p) => ({ id: p, label: PERMISSION_LABELS[p] || p }))} value={form.permissions} onChange={set("permissions")} testPrefix="user-perm" />}</div>
                </>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={submit} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-user-btn">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
